import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MoreHorizontal, X } from 'lucide-react';
import { navigationItems } from './navigation';
import { PAGE_TO_PATH } from '../../App';

export function BottomNav({ restaurant, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const moreButtonRef = useRef(null);

  const currentPage = Object.entries(PAGE_TO_PATH).find(([, p]) => p === location.pathname)?.[0] || 'dashboard';
  const isOverview = ['admin', 'general_manager'].includes(user?.role) && !restaurant;
  const allItems = navigationItems(user?.role, !!restaurant);
  const primary = isOverview ? ['dashboard', 'people', 'audits', 'settings'] : ['dashboard', 'schedule', 'clockin', 'approvals'];
  const items = primary.map(id => allItems.find(item => item.id === id)).filter(Boolean);
  const moreItems = allItems.filter(item => !primary.includes(item.id));
  const moreIsActive = moreItems.some(item => item.id === currentPage);

  // Close drawer when route changes
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e) { if (e.key === 'Escape') closeDrawer(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  function closeDrawer() {
    setDrawerOpen(false);
    // Return focus to the More button so keyboard users aren't lost
    setTimeout(() => moreButtonRef.current?.focus(), 50);
  }

  function handleMoreNav(id) {
    navigate(PAGE_TO_PATH[id]);
    setDrawerOpen(false);
  }

  return (
    <>
      <div className="bottom-nav">
        <div className="bottom-nav-items">
          {items.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`bottom-nav-item ${currentPage === id ? 'active' : ''}`}
              onClick={() => navigate(PAGE_TO_PATH[id])}
              aria-label={label}
              aria-current={currentPage === id ? 'page' : undefined}
            >
              <span className="bottom-nav-item-icon"><Icon size={22} /></span>
              {id === 'settings' ? 'Settings' : label}
            </button>
          ))}

          {/* More button — always last */}
          <button
            ref={moreButtonRef}
            className={`bottom-nav-item ${moreIsActive || drawerOpen ? 'active' : ''}`}
            onClick={() => drawerOpen ? closeDrawer() : setDrawerOpen(true)}
            aria-label="More navigation options"
            aria-expanded={drawerOpen}
            aria-haspopup="dialog"
          >
            <span className="bottom-nav-item-icon"><MoreHorizontal size={22} /></span>
            More
          </button>
        </div>
      </div>

      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="bottom-drawer-backdrop"
            onClick={closeDrawer}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div
            className="bottom-drawer"
            role="dialog"
            aria-label="More navigation options"
            aria-modal="true"
          >
            <div className="bottom-drawer-handle" aria-hidden="true" />

            <div className="bottom-drawer-header">
              <span className="bottom-drawer-title">More</span>
              <button
                className="bottom-drawer-close"
                onClick={closeDrawer}
                aria-label="Close navigation drawer"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {moreItems.map(({ id, label, Icon }) => (
              <button
                key={id}
                className={`bottom-drawer-item ${currentPage === id ? 'active' : ''}`}
                onClick={() => handleMoreNav(id)}
                aria-current={currentPage === id ? 'page' : undefined}
              >
                <Icon size={22} aria-hidden="true" />
                {id === 'settings' ? 'Settings' : label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
